/**
 * Éditeur du catalogue de cadeaux (réutilisabilité — Étape 9B).
 * Affiché dans les réglages « Récompenses » quand l'entreprise est en mode cadeaux.
 * CRUD simple : libellé FR/EN, coût en BizCoins, stock (vide = illimité), actif.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, Plus, Pencil } from "lucide-react";
import { bizCoinsApi, type BizGift } from "../lib/api";
import { Button } from "./ui/button";

const NAVY = "#384872";
const emptyDraft = { labelFr: "", labelEn: "", costCoins: "", stock: "", active: true };
type Draft = typeof emptyDraft;

export function GiftCatalogEditor() {
  const { t } = useTranslation();
  const [gifts, setGifts] = useState<BizGift[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => bizCoinsApi.listGifts().then(setGifts).catch(() => setGifts([]));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditingId(null); setDraft({ ...emptyDraft }); };
  const startEdit = (g: BizGift) => {
    setEditingId(g.id);
    setDraft({ labelFr: g.labelFr, labelEn: g.labelEn, costCoins: String(g.costCoins), stock: g.stock != null ? String(g.stock) : "", active: g.active });
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const body = {
        labelFr: draft.labelFr.trim(),
        labelEn: draft.labelEn.trim(),
        costCoins: Number(draft.costCoins) || 0,
        stock: draft.stock === "" ? null : Number(draft.stock),
        active: draft.active,
      };
      if (editingId) await bizCoinsApi.updateGift(editingId, body);
      else await bizCoinsApi.createGift(body);
      setDraft(null); setEditingId(null);
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm(t("bizGifts.confirmDelete"))) return;
    await bizCoinsApi.deleteGift(id);
    await load();
  };

  const inp = "w-full rounded-lg border px-2 py-1.5 text-sm";
  const inpStyle = { borderColor: "#E4E7EF", color: NAVY };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-sm" style={{ color: NAVY }}>{t("bizGifts.title")}</h4>
        {!draft && <Button size="sm" variant="outline" onClick={startNew}><Plus className="h-4 w-4 mr-1" />{t("bizGifts.new")}</Button>}
      </div>

      {gifts.length === 0 && !draft && <p className="text-sm text-gray-500">{t("bizGifts.empty")}</p>}

      <ul className="space-y-2">
        {gifts.map((g) => (
          <li key={g.id} className="flex items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: "#E4E7EF", opacity: g.active ? 1 : 0.55 }}>
            <span className="flex-1 text-sm font-medium" style={{ color: NAVY }}>{g.labelFr}</span>
            <span className="text-sm tabular-nums" style={{ color: "#926B00" }}>{g.costCoins} BC</span>
            <span className="text-xs text-gray-400">{g.stock != null ? t("bizGifts.stockN", { n: g.stock }) : t("bizGifts.stockUnlimited")}</span>
            <button onClick={() => startEdit(g)} className="p-1 text-gray-400 hover:text-gray-700"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(g.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>

      {draft && (
        <div className="mt-3 rounded-lg border p-3 space-y-2" style={{ borderColor: "#988EF2" }}>
          <div className="grid grid-cols-2 gap-2">
            <input className={inp} style={inpStyle} placeholder={t("bizGifts.labelFr")} value={draft.labelFr} onChange={(e) => setDraft({ ...draft, labelFr: e.target.value })} />
            <input className={inp} style={inpStyle} placeholder={t("bizGifts.labelEn")} value={draft.labelEn} onChange={(e) => setDraft({ ...draft, labelEn: e.target.value })} />
            <input type="number" className={inp} style={inpStyle} placeholder={t("bizGifts.cost")} value={draft.costCoins} onChange={(e) => setDraft({ ...draft, costCoins: e.target.value })} />
            <input type="number" className={inp} style={inpStyle} placeholder={t("bizGifts.stockPlaceholder")} value={draft.stock} onChange={(e) => setDraft({ ...draft, stock: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-sm" style={{ color: NAVY }}>
            <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
            {t("bizGifts.active")}
          </label>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={busy} className="text-white" style={{ background: "#5A4BBE" }}>{t("common.save")}</Button>
            <Button size="sm" variant="ghost" onClick={() => { setDraft(null); setEditingId(null); }}>{t("common.cancel")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
